import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import { JurisdictionProvider } from './context/JurisdictionContext.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import RoleRoute from './components/RoleRoute.jsx';
import DefaultWebLayout from './layouts/DefaultWebLayout.jsx';
import { ROLES } from './constants/roles.js';
import './App.scss';

// Auth screens are part of the first paint path for anonymous users — import
// them eagerly so signing in never waits on a chunk fetch.
import { LoginPage } from './pages/login/index.jsx';

const DashboardPage = lazy(() => import('./pages/dashboard/index.jsx'));
const UsersPage = lazy(() => import('./pages/users/index.jsx'));
const SettingsPage = lazy(() => import('./pages/settings/index.jsx'));
const CapturePage = lazy(() => import('./pages/capture/index.jsx'));
const AnalysisPage = lazy(() => import('./pages/analysis/index.jsx'));
const ChatsPage = lazy(() => import('./pages/chats/index.jsx'));
const ListingsPage = lazy(() => import('./pages/listings/index.jsx'));
const RegistryPage = lazy(() => import('./pages/registry/index.jsx'));
const MandatesPage = lazy(() => import('./pages/mandates/index.jsx'));
const EnrichmentPage = lazy(() => import('./pages/enrichment/index.jsx'));
const MatchingPage = lazy(() => import('./pages/matching/index.jsx'));
const PacksPage = lazy(() => import('./pages/packs/index.jsx'));
const ScanLandingPage = lazy(() => import('./pages/scan/index.jsx'));
const DealsPage = lazy(() => import('./pages/deals/index.jsx'));
const RefreshPage = lazy(() => import('./pages/refresh/index.jsx'));
const NetworkPage = lazy(() => import('./pages/network/index.jsx'));
const ReachPage = lazy(() => import('./pages/reach/index.jsx'));
const ReachDossierPage = lazy(() => import('./pages/reach/dossier/index.jsx'));
const HelpPage = lazy(() => import('./pages/help/index.jsx'));
const ForbiddenPage = lazy(() => import('./pages/forbidden/index.jsx'));
const NotFoundPage = lazy(() => import('./pages/not-found/index.jsx'));

// Pages whose design nominates a layout other than the default get their own
// route group. MapPropertyCaptureLayout additionally loads Leaflet's stylesheet.
const MapPropertyCaptureLayout = lazy(() => import('./layouts/MapPropertyCaptureLayout.jsx'));
const ConversationalChatLogsLayout = lazy(
  () => import('./layouts/ConversationalChatLogsLayout.jsx')
);
const MatchingScoringPanelLayout = lazy(() => import('./layouts/MatchingScoringPanelLayout.jsx'));

const Loader = () => (
  <div className="d-flex justify-content-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <JurisdictionProvider>
        {/*
        The app lives under /app (Vite base '/app/'); "/" is the public marketing
        microsite served by the backend. basename lets every Link, redirect and
        guard keep writing "/login", "/listings", … while the browser sees them
        under /app. BASE_URL is '/app/' — strip the trailing slash for React Router.
      */}
        <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, '')}>
          <Suspense fallback={<Loader />}>
            <Routes>
              {/*
              The developer's QR landing. Public AND layout-less — the person
              scanning has no account and arrived from a printed page, so it
              renders outside every shell rather than inside chrome that offers
              them a Login button they cannot use.
            */}
              <Route path="p/:token" element={<ScanLandingPage />} />

              {/*
              Sign-in is layout-less. It carries its own branding panel and
              copyright, so the shell's navbar would duplicate that — and its
              menu links only lead to guarded pages the signed-out visitor
              cannot open anyway.

              There is no /register: LandOS is internal and an Admin creates
              accounts in User Management. A stray link here would 404, which
              is the honest outcome — the endpoint behind it is gone too.
            */}
              <Route path="login" element={<LoginPage />} />

              {/*
              Map Property Capture is PUBLIC: per scope a Contact submits a
              parcel without ever signing in (page permission "View: Any User,
              Contact, Analyst, Principal"). An Analyst who happens to be signed
              in is attributed automatically by the API.
            */}
              <Route element={<MapPropertyCaptureLayout />}>
                <Route path="capture" element={<CapturePage />} />
                {/*
                Location Analysis reuses the map-capture layout (it loads
                Leaflet's stylesheet) but, unlike public capture, is staff-only —
                "View: Analyst, Principal, Admin" — so it takes a RoleRoute.
              */}
                <Route
                  element={<RoleRoute allow={[ROLES.ANALYST, ROLES.PRINCIPAL, ROLES.ADMIN]} />}
                >
                  <Route path="analysis/:listingId" element={<AnalysisPage />} />
                </Route>
              </Route>

              {/*
              Chat monitoring and the Sourced Listings Worklist both nominate
              this layout, and both are "View: Analyst, Principal". Curation
              itself is Analyst-only — enforced by the API and reflected in the
              worklist's controls, not by the route.
            */}
              <Route element={<ConversationalChatLogsLayout />}>
                <Route
                  element={<RoleRoute allow={[ROLES.ANALYST, ROLES.PRINCIPAL, ROLES.ADMIN]} />}
                >
                  <Route path="chats" element={<ChatsPage />} />
                  <Route path="listings" element={<ListingsPage />} />
                  <Route path="registry" element={<RegistryPage />} />
                  <Route path="mandates" element={<MandatesPage />} />
                  <Route path="deals" element={<DealsPage />} />
                  {/* "View: Analyst, Principal" — the queue/master-run actions
                    are gated further, inside the page and on the API. */}
                  <Route path="refresh" element={<RefreshPage />} />
                </Route>

                {/*
                Location Context Enrichment is "View: Any User, Contact,
                Analyst, Principal" — broader than its neighbours, so it takes
                ProtectedRoute rather than the RoleRoute above. Editing is
                split by WHAT is edited (Principal: context setup only), which
                the page and the API enforce; a route guard cannot express it.
              */}
                <Route element={<ProtectedRoute />}>
                  <Route path="enrich" element={<EnrichmentPage />} />
                </Route>
              </Route>

              {/*
              The Matching panel nominates its own layout in the design
              (_matching-scoring-panel-layout), rather than the chat-logs shell
              its neighbours use. "View: Analyst, Principal / Edit: Analyst,
              Principal" — both may run a match, so one RoleRoute covers it.
            */}
              <Route element={<MatchingScoringPanelLayout />}>
                <Route
                  element={<RoleRoute allow={[ROLES.ANALYST, ROLES.PRINCIPAL, ROLES.ADMIN]} />}
                >
                  <Route path="matching" element={<MatchingPage />} />
                  {/*
                  Pack Builder's design nominates the map-capture layout (it
                  renders an annotated satellite frame), but that layout is
                  public and this page is "View: Analyst, Principal". It sits in
                  the guarded group; the map frame it needs is inside the page.
                */}
                  <Route path="packs" element={<PacksPage />} />
                </Route>
              </Route>

              {/* Everything else renders inside the default layout chrome. */}
              <Route element={<DefaultWebLayout />}>
                {/*
                Public routes MUST be registered before the guarded groups, so
                they still resolve for anonymous users — otherwise a redirect
                falls through to the 404 catch-all and the app becomes
                unreachable. /login, the target every guard redirects to, is
                registered layout-less further up.
              */}
                <Route path="forbidden" element={<ForbiddenPage />} />

                {/*
                Help is PUBLIC, and deliberately so. Being unable to get in is
                one of the moments you most need the guide — putting it behind
                the guard would mean the "why can't I sign in / what is my role
                allowed to do" answers were only readable by people who no
                longer need them. It documents behaviour, not data.
              */}
                <Route path="help" element={<HelpPage />} />

                {/*
                "/" is the signed-in overview: what the pipeline looks like and
                what is waiting on you.

                It is NOT the design's DefaultMenuItem (that is Listings — see
                DEFAULT_MENU_PATH in menu-data.js, still what the guards fall
                back to), and none of the design's twelve role objectives is a
                dashboard. This is a deliberate addition on top of the design:
                eleven task screens with no overview means the first question of
                the day — "what needs me?" — can only be answered by opening all
                eleven. What it must never become is the old hub: a page of
                links to the navbar's own items, dressed as a dashboard.
              */}
                <Route element={<ProtectedRoute />}>
                  <Route index element={<DashboardPage />} />
                </Route>

                {/*
                The Relationship Network Explorer nominates the DEFAULT layout
                in its design, unlike its neighbours, so it sits here rather
                than in the chat-logs group. "View: Principal, Analyst";
                editing relationships is Principal-only and is enforced by the
                API, which a route guard cannot express.
              */}
                <Route
                  element={<RoleRoute allow={[ROLES.PRINCIPAL, ROLES.ANALYST, ROLES.ADMIN]} />}
                >
                  <Route path="network" element={<NetworkPage />} />
                  <Route path="reach" element={<ReachPage />} />
                  <Route path="reach/:orgId" element={<ReachDossierPage />} />
                </Route>

                {/* Admin-only routes (NFR-4). The API enforces this independently. */}
                <Route element={<RoleRoute allow={[ROLES.ADMIN]} />}>
                  <Route path="admin/users" element={<UsersPage />} />
                  <Route path="admin/settings" element={<SettingsPage />} />
                </Route>

                {/*
                Remaining feature routes (/listings, /matching, …) are added by
                their own use cases, each wrapping itself in the layout and the
                RoleRoute its scope permission rule calls for.
              */}

                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </JurisdictionProvider>
    </AuthProvider>
  );
}

export default App;
